import React, {
  useRef, useCallback, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Container } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import SupportIcon from '@mui/icons-material/Support';
import InfoIcon from '@mui/icons-material/Info';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import AppleIcon from '@mui/icons-material/Apple';
import CampaignIcon from '@mui/icons-material/Campaign';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MessageBox from 'src/components/MessageBox';
import Page from 'src/components/Page';
import Header from 'src/components/View/Header';
import HelpAndInformationView from 'src/views/pages/HelpAndInformation/HelpAndInformationView';
import GettingStartedSection from 'src/views/pages/HelpAndInformation/GettingStartedSection';
import FullWebinarSection from 'src/views/pages/HelpAndInformation/FullWebinarSection';
import TransitionGuideBanner from 'src/views/pages/HelpAndInformation/TransitionGuideBanner';
// Bundle Parties banner retired along with its help section (see
// companyOverrides); Voice Calling banner demoted to a regular help
// section in helpSectionsData.
// import BundlePartiesBanner from 'src/views/pages/HelpAndInformation/BundlePartiesBanner';
// import VoiceCallingBanner from 'src/views/pages/HelpAndInformation/VoiceCallingBanner';
import HostAppBanner from 'src/views/pages/HelpAndInformation/HostAppBanner';
import WebinarReplayBanner from 'src/views/pages/HelpAndInformation/WebinarReplayBanner';
import GoogleDocLinkModal from 'src/views/pages/HelpAndInformation/GoogleDocLinkModal';
import WhatsNewModal from 'src/containers/WhatsNewModal';
import {
  fetchWhatsNewHistory,
  closeWhatsNewHistory,
} from 'src/actions/accountActions';
import helpers from 'src/helpers';
import { PERMISSIONS } from 'src/constants';

import helpAndInformationStyles from 'src/views/pages/HelpAndInformation/helpAndInformation.style';

const { PERMISSIONS_SYSTEM_CODE } = PERMISSIONS;

// URL salespeople share with their hosts to download the Host App. The page
// auto-detects the visitor's OS and shows the matching store button.
const HOST_APP_SHARE_URL = 'https://www.connectionpointapp.com/host';

const TRANSITION_GUIDE = {
  title: 'Transition Guide',
  url: 'https://docs.google.com/document/d/1rz-liSfcc8a6ehqqV4TmPTDyqTGKmwINdlTLQulPxlo/preview',
};

const GETTING_STARTED_GUIDES = [
  {
    title: 'Getting Started with CP',
    url: 'https://docs.google.com/document/d/1SUTWj5Ano12xlJEAAzw_3QQ030NSJAI0YmVPwHnqHkQ',
  },
  {
    title: 'Getting Started with CP Events',
    url: 'https://docs.google.com/document/d/1RQeptPM1cQdHj5A4IPu9o1fWwL1CCMOoSSrvp-smKCE',
  },
];

function AndroidHeadIcon({ style = {} }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      style={{ width: '1em', height: '1em', ...style }}
    >
      <path d="M6 18c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V19h1c.55 0 1-.45 1-1V8H6v10zM3.5 8C2.67 8 2 8.67 2 9.5v7c0 .83.67 1.5 1.5 1.5S5 17.33 5 16.5v-7C5 8.67 4.33 8 3.5 8zm17 0c-.83 0-1.5.67-1.5 1.5v7c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5v-7c0-.83-.67-1.5-1.5-1.5zm-4.97-5.84l1.3-1.3c.2-.2.2-.51 0-.71-.2-.2-.51-.2-.71 0l-1.48 1.48C13.85 1.23 12.95 1 12 1c-.96 0-1.86.23-2.66.63L7.85.15c-.2-.2-.51-.2-.71 0-.2.2-.2.51 0 .71l1.31 1.31C6.97 3.26 6 5.01 6 7h12c0-1.99-.97-3.75-2.47-4.84zM10 5H9V4h1v1zm5 0h-1V4h1v1z" />
    </svg>
  );
}

AndroidHeadIcon.propTypes = {
  style: PropTypes.object,
};

const BREADCRUMBS = [
  {
    link: '/',
    title: 'Settings'
  },
  {
    isCurrent: true,
    title: 'Help & Information'
  },
];

function HelpAndInformation({
  classes,
}) {
  const helpViewRef = useRef(null);
  const dispatch = useDispatch();

  const [googleDocModal, setGoogleDocModal] = useState({
    isVisible: false,
    docUrl: '',
    docTitle: '',
  });

  const [hostShareLinkCopied, setHostShareLinkCopied] = useState(false);

  const {
    primaryCompany,
    userTCRRegistration,
    textingProvider,
    userPhoneNumbersInfo,
    salespersonId,
    email: userEmail,
    gmail: userGmail,
    userRole,
  } = useSelector((state) => state.account?.user || {});

  const {
    mobileAppIosUrl,
    mobileAppAndroidUrl,
    // zoomClassIntroUrl,
    // zoomClassEventsUrl,
    cpTextingGuideMonthsToShow,
    excludedSalespersonIds,
    helpPageWebinarVideoUrl,
    helpPageWebinarVideoDescription,
    extendedMonthsSalespersonIds,
    whatsNewHistory,
    hostAppIosUrl,
    hostAppAndroidUrl,
  } = useSelector((state) => state.account || {});

  const {
    status: tcrStatus = null,
  } = userTCRRegistration || {};

  const handleOpenWhatsNewHistory = useCallback(() => {
    dispatch(fetchWhatsNewHistory());
  }, [dispatch]);

  const handleCloseWhatsNewHistory = useCallback(() => {
    dispatch(closeWhatsNewHistory());
  }, [dispatch]);

  const showMobileApps = tcrStatus
    && tcrStatus !== 'not_started'
    && (mobileAppIosUrl || mobileAppAndroidUrl);

  // Host App section: CP texting users only, and only when at least one
  // store URL is populated (so the header never shows with no buttons).
  // Each button is independently gated on its own URL below.
  const showHostApp = textingProvider === 'CP'
    && (!!hostAppIosUrl || !!hostAppAndroidUrl);
  // const showZoomClasses = zoomClassIntroUrl || zoomClassEventsUrl;
  const showGettingStartedGuides = textingProvider === 'CP';

  // Host App banner (and the webinar replay banner above it): CP texting users only.
  const showHostAppBanner = textingProvider === 'CP';

  const { permissions: userRolePermissions } = userRole || {};
  const showWhatsNew = helpers.permissions.doesUserHavePermission(
    PERMISSIONS_SYSTEM_CODE.WHATS_NEW_VIEW,
    userRolePermissions,
  );

  // Visibility for texting guide content (transition guide, overview, webinar)
  const showTextingGuideContent = useMemo(() => {
    // 1. TCR status must be truthy
    if (!tcrStatus) return false;

    // 2. User must not be in the exclusion list
    if (excludedSalespersonIds && salespersonId) {
      const excludedIds = excludedSalespersonIds
        .split(',')
        .map((id) => parseInt(id.trim(), 10));
      if (excludedIds.includes(salespersonId)) return false;
    }

    // 3. Time-based: earlier of phone/TCR createdAt within X months
    let months = parseInt(cpTextingGuideMonthsToShow, 10) || 3;

    // Check for per-user override
    if (extendedMonthsSalespersonIds && salespersonId) {
      const pairs = extendedMonthsSalespersonIds
        .split(',')
        .map((p) => p.trim());
      for (let i = 0; i < pairs.length; i += 1) {
        const parts = pairs[i].split(':').map((v) => v.trim());
        if (parseInt(parts[0], 10) === salespersonId) {
          months = parseInt(parts[1], 10) || months;
          break;
        }
      }
    }

    const phoneCreated = userPhoneNumbersInfo?.createdAt
      ? new Date(userPhoneNumbersInfo.createdAt)
      : null;
    const tcrCreated = userTCRRegistration?.createdAt
      ? new Date(userTCRRegistration.createdAt)
      : null;

    let earliestDate = null;
    if (phoneCreated && tcrCreated) {
      earliestDate = phoneCreated < tcrCreated
        ? phoneCreated
        : tcrCreated;
    } else {
      earliestDate = phoneCreated || tcrCreated;
    }

    if (!earliestDate) return false;

    const cutoff = new Date();
    cutoff.setMonth(cutoff.getMonth() - months);
    return earliestDate >= cutoff;
  }, [
    tcrStatus,
    excludedSalespersonIds,
    salespersonId,
    cpTextingGuideMonthsToShow,
    extendedMonthsSalespersonIds,
    userPhoneNumbersInfo,
    userTCRRegistration,
  ]);

  const handleCopyHostShareLink = useCallback(async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(HOST_APP_SHARE_URL);
      } else {
        // Fallback for browsers without the async clipboard API
        const textArea = document.createElement('textarea');
        textArea.value = HOST_APP_SHARE_URL;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setHostShareLinkCopied(true);
      setTimeout(() => setHostShareLinkCopied(false), 2000);
    } catch (err) {
      // Silently ignore — the URL is visible for manual copy as a fallback
    }
  }, []);

  const handleOpenHostShareLink = useCallback(() => {
    window.open(HOST_APP_SHARE_URL, '_blank', 'noopener,noreferrer');
  }, []);

  const handleOpenGoogleDocModal = useCallback((guide) => {
    setGoogleDocModal({
      isVisible: true,
      docUrl: guide.url,
      docTitle: guide.title,
    });
  }, []);

  const handleOpenTransitionGuide = useCallback(() => {
    handleOpenGoogleDocModal(TRANSITION_GUIDE);
  }, [handleOpenGoogleDocModal]);

  const handleCloseGoogleDocModal = useCallback(() => {
    setGoogleDocModal({
      isVisible: false,
      docUrl: '',
      docTitle: '',
    });
  }, []);

  const messageForMessageBox = (
    <div className={classes.messageForMessageBox}>
      <div className="main-content">
        <div className="version-section">
          <div className="version-header">
            <div className="version-title">
              <InfoIcon />
              Connection Point™ Version 4.01
            </div>
            <div className="version-badge">Latest</div>
          </div>
          <div className="company-info">
            <div className="info-item">
              © Connection Point, Inc.
            </div>
            <div className="info-item">
              <a
                target="_blank"
                href="https://drive.google.com/file/d/1-4JKQNnyGQrAT_J_8G5Nbuups4rVqyOR"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </div>
            <div className="info-item">
              <a
                target="_blank"
                href="https://drive.google.com/file/d/101Y30SYGunh3-Q2_itNKhnSzMqeHuj52"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
            </div>
          </div>
          {showMobileApps && (
            <div className="mobile-apps-section">
              <div className="mobile-apps-header">
                <PhoneIphoneIcon style={{ fontSize: '14px' }} />
                CP Chat Mobile App
              </div>
              <div className="mobile-apps-links">
                {mobileAppIosUrl && (
                  <a
                    target="_blank"
                    href={mobileAppIosUrl}
                    rel="noopener noreferrer"
                    className="app-store-link"
                  >
                    <AppleIcon style={{ fontSize: '16px' }} />
                    iOS App
                  </a>
                )}
                {mobileAppAndroidUrl && (
                  <a
                    target="_blank"
                    href={mobileAppAndroidUrl}
                    rel="noopener noreferrer"
                    className="app-store-link"
                  >
                    <AndroidHeadIcon style={{ fontSize: '16px' }} />
                    Android App
                  </a>
                )}
              </div>
            </div>
          )}
          {showHostApp && (
            <div className="host-app-section">
              <div className="host-app-header">
                <PhoneIphoneIcon style={{ fontSize: '14px' }} />
                Host App
              </div>
              <div className="host-app-links">
                {hostAppIosUrl && (
                  <a
                    target="_blank"
                    href={hostAppIosUrl}
                    rel="noopener noreferrer"
                    className="app-store-link"
                  >
                    <AppleIcon style={{ fontSize: '16px' }} />
                    iOS App
                  </a>
                )}
                {hostAppAndroidUrl && (
                  <a
                    target="_blank"
                    href={hostAppAndroidUrl}
                    rel="noopener noreferrer"
                    className="app-store-link"
                  >
                    <AndroidHeadIcon style={{ fontSize: '16px' }} />
                    Android App
                  </a>
                )}
              </div>
              <div className="host-share-label">
                Send this link to your hosts:
              </div>
              <div className="host-share-row">
                <span className="host-share-url" title={HOST_APP_SHARE_URL}>
                  {HOST_APP_SHARE_URL}
                </span>
                <button
                  type="button"
                  className="host-share-btn"
                  onClick={handleCopyHostShareLink}
                  title="Copy link"
                >
                  {hostShareLinkCopied ? (
                    <>
                      <CheckIcon style={{ fontSize: '14px' }} />
                      Copied!
                    </>
                  ) : (
                    <>
                      <ContentCopyIcon style={{ fontSize: '14px' }} />
                      Copy
                    </>
                  )}
                </button>
                <button
                  type="button"
                  className="host-share-btn"
                  onClick={handleOpenHostShareLink}
                  title="Open in new tab"
                >
                  <OpenInNewIcon style={{ fontSize: '14px' }} />
                  Open
                </button>
              </div>
            </div>
          )}
          {showWhatsNew && (
            <div className="whats-new-section">
              <div className="whats-new-header">
                <CampaignIcon style={{ fontSize: '14px' }} />
                What&apos;s New in CP
              </div>
              <div className="whats-new-item">
                <div className="whats-new-text">
                  See the latest features and updates.
                </div>
                <button
                  type="button"
                  className="whats-new-link"
                  onClick={handleOpenWhatsNewHistory}
                >
                  View What&apos;s New &rarr;
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="support-section">
          <div className="support-header">
            <SupportIcon />
            Support & Resources
          </div>
          <div className="support-grid">
            <div className="support-item">
              <div className="support-label">Text for Support</div>
              <div className="support-content">
                <span className="phone-number">317-456-5610</span>
              </div>
            </div>
            <div className="support-item">
              <div className="support-label">Facebook Community</div>
              <div className="support-content">
                <a
                  target="_blank"
                  href="https://www.facebook.com/groups/ConnectionPoint"
                  rel="noopener noreferrer"
                >
                  Connection Point Group
                </a>
              </div>
            </div>
          </div>

          {showGettingStartedGuides && (
            <div className="guides-section">
              <div className="guides-header">
                <MenuBookIcon style={{ fontSize: '14px' }} />
                Getting Started Guides
              </div>
              {GETTING_STARTED_GUIDES.map((guide) => (
                <div className="guide-item" key={guide.url}>
                  <div className="guide-name">{guide.title}</div>
                  <button
                    type="button"
                    className="guide-link"
                    onClick={() => handleOpenGoogleDocModal(guide)}
                  >
                    View
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Live Interactive Zoom Classes - Commented out, replaced by Getting Started Guides above
          {
            showZoomClasses && (
              <div className="classes-section">
                <div className="classes-header">
                  <SchoolIcon style={{ fontSize: '14px' }} />
                  Live Interactive Zoom Classes
                </div>
                {zoomClassIntroUrl && (
                  <div className="class-item">
                    <div className="class-name">Introduction Class</div>
                    <a
                      target="_blank"
                      href={zoomClassIntroUrl}
                      rel="noopener noreferrer"
                      className="class-link"
                    >
                      Sign Up
                    </a>
                  </div>
                )}
                {
                  zoomClassEventsUrl && (
                    <div className="class-item">
                      <div className="class-name">Events Module Class</div>
                      <a
                        target="_blank"
                        href={zoomClassEventsUrl}
                        rel="noopener noreferrer"
                        className="class-link"
                      >
                        Sign Up
                      </a>
                    </div>
                  )
                }
              </div>
            )
          }
          */}
        </div>
      </div>
    </div>
  );

  return (
    <Page
      className={classes.root}
      title="Help & Information"
    >
      <Container maxWidth={false}>
        <Header
          title="Help & Information"
          breadcrumbs={BREADCRUMBS}
          message={(
            <MessageBox
              message={messageForMessageBox}
              type="type-1"
              paddingTop="20"
              paddingBottom="0"
              visible
            />
          )}
        />
        {showTextingGuideContent && (
          <>
            <Box mt={3}>
              <TransitionGuideBanner
                onOpenGuide={handleOpenTransitionGuide}
              />
            </Box>
            <Box mt={2}>
              <GettingStartedSection />
            </Box>
            {helpPageWebinarVideoUrl && (
              <Box mt={2}>
                <FullWebinarSection
                  videoUrl={helpPageWebinarVideoUrl}
                  videoDescription={helpPageWebinarVideoDescription}
                />
              </Box>
            )}
          </>
        )}
        {showHostAppBanner && (
          <>
            <Box mt={2}>
              <WebinarReplayBanner />
            </Box>
            <Box mt={2}>
              <HostAppBanner />
            </Box>
          </>
        )}
        <Box mt={3}>
          <HelpAndInformationView
            ref={helpViewRef}
            primaryCompany={primaryCompany}
            textingProvider={textingProvider}
          />
        </Box>
      </Container>
      <GoogleDocLinkModal
        isVisible={googleDocModal.isVisible}
        onClose={handleCloseGoogleDocModal}
        docUrl={googleDocModal.docUrl}
        docTitle={googleDocModal.docTitle}
        userEmail={userEmail}
        userGmail={userGmail}
      />
      <WhatsNewModal
        historyMode
        historyFeatures={whatsNewHistory?.features || []}
        open={whatsNewHistory?.isOpen || false}
        onClose={handleCloseWhatsNewHistory}
      />
    </Page>
  );
}

HelpAndInformation.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(HelpAndInformation, helpAndInformationStyles, { withTheme: true });
